import { Draggable, type DropResult } from "@hello-pangea/dnd";
import { Plus, Target } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import {
  KanbanBoard,
  KanbanColumn,
  KanbanDropZone,
} from "@/components/atomic-crm/misc/Kanban";
import { SegmentedControl } from "@/components/atomic-crm/tasks/SegmentedControl";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMoneyFormat } from "@/hooks/use-money-format";
import { cn } from "@/lib/utils";

import { EmptyState, LoadingCards } from "./components";
import { ConvertToDealDialog } from "./ConvertToDealDialog";
import { toIsoDay } from "./dates";
import {
  isOpportunityStage,
  OPPORTUNITY_STAGES,
  opportunityStageLabelKey,
} from "./labels";
import { useStageChange } from "./marketHooks";
import { OpportunityCard } from "./OpportunityCard";
import { OpportunityForm } from "./OpportunityForm";
import {
  groupOpportunitiesByStage,
  sortForList,
  STAGE_COLOR,
  totalValue,
  type OpportunityColumns,
} from "./opportunities";
import type { MarketOpportunity } from "./types";

type View = "list" | "board";

/**
 * Mercado > Prospectos: potential clients, tenders, partners and ideas spotted in
 * the research, as a list (default) or a kanban by stage (?vista=tablero).
 */
export const OpportunitiesView = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const view: View = params.get("vista") === "tablero" ? "board" : "list";
  const [editing, setEditing] = useState<MarketOpportunity | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [converting, setConverting] = useState<MarketOpportunity | null>(null);
  const today = toIsoDay(new Date());
  const { data, isPending, error } = useGetList<MarketOpportunity>(
    "market_opportunities",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "updated_at", order: "DESC" },
    },
  );

  const openForm = (opportunity: MarketOpportunity | null) => {
    setEditing(opportunity);
    setFormOpen(true);
  };
  const cardProps = {
    today,
    onEdit: openForm,
    onConvert: setConverting,
  };
  const newLabel = translate("market.opportunities.new");

  return (
    <section className="space-y-3 md:space-y-4">
      <h2 className="sr-only">{translate("market.opportunities.heading")}</h2>
      <div className="flex items-center justify-between gap-2">
        <SegmentedControl<View>
          value={view}
          onChange={(next) =>
            setParams(next === "board" ? { vista: "tablero" } : {}, {
              replace: true,
            })
          }
          label={translate("market.opportunities.view")}
          options={[
            {
              value: "list",
              label: translate("market.opportunities.view_list"),
            },
            {
              value: "board",
              label: translate("market.opportunities.view_board"),
            },
          ]}
          glass={isMobile}
          size="sm"
          className="w-52"
        />
        <Button
          type="button"
          onClick={() => openForm(null)}
          aria-label={isMobile ? newLabel : undefined}
          title={isMobile ? newLabel : undefined}
          className={cn(
            "btn-save shrink-0 rounded-full",
            isMobile ? "size-9 p-0" : "px-4",
          )}
        >
          <Plus aria-hidden className={isMobile ? "size-5" : "size-4"} />
          {isMobile ? null : newLabel}
        </Button>
      </div>
      {isPending ? (
        <LoadingCards />
      ) : error ? (
        <EmptyState icon={Target} title={translate("market.error")} />
      ) : !data.length ? (
        <EmptyState
          icon={Target}
          title={translate("market.opportunities.empty_all")}
          hint={translate("market.opportunities.empty_hint")}
          action={
            <Button
              type="button"
              className="btn-save rounded-full px-4"
              onClick={() => openForm(null)}
            >
              <Plus aria-hidden className="size-4" />
              {newLabel}
            </Button>
          }
        />
      ) : view === "board" ? (
        <OpportunityBoard opportunities={data} cardProps={cardProps} />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
          {sortForList(data).map((opportunity, index) => (
            <OpportunityCard
              key={opportunity.id}
              opportunity={opportunity}
              index={index}
              {...cardProps}
            />
          ))}
        </div>
      )}
      <OpportunityForm
        open={formOpen}
        onOpenChange={setFormOpen}
        opportunity={editing}
      />
      {converting ? (
        <ConvertToDealDialog
          open
          onOpenChange={(open) => {
            if (!open) setConverting(null);
          }}
          opportunity={converting}
        />
      ) : null}
    </section>
  );
};

interface BoardProps {
  opportunities: MarketOpportunity[];
  cardProps: {
    today: string;
    onEdit: (opportunity: MarketOpportunity) => void;
    onConvert: (opportunity: MarketOpportunity) => void;
  };
}

/** Kanban by stage (shared misc/Kanban: glass columns, snap on a phone). */
const OpportunityBoard = ({ opportunities, cardProps }: BoardProps) => {
  const translate = useTranslate();
  const { formatCompactMoney } = useMoneyFormat();
  const changeStage = useStageChange();
  const columns: OpportunityColumns = useMemo(
    () => groupOpportunitiesByStage(opportunities),
    [opportunities],
  );

  const onDragEnd = ({ draggableId, destination }: DropResult) => {
    const stage = destination?.droppableId;
    const opportunity = opportunities.find(
      (entry) => String(entry.id) === draggableId,
    );
    if (opportunity && isOpportunityStage(stage))
      changeStage(opportunity, stage);
  };

  return (
    <KanbanBoard onDragEnd={onDragEnd} className="mi-opportunity-board">
      {OPPORTUNITY_STAGES.map((stage) => {
        const list = columns[stage];
        const total = totalValue(list);
        return (
          <KanbanColumn
            key={stage}
            title={translate(opportunityStageLabelKey(stage))}
            count={list.length}
            color={STAGE_COLOR[stage]}
            meta={total ? formatCompactMoney(total) : undefined}
          >
            <KanbanDropZone
              droppableId={stage}
              isEmpty={list.length === 0}
              emptyLabel={translate("market.opportunities.empty_column")}
            >
              {list.map((opportunity, index) => (
                <Draggable
                  key={opportunity.id}
                  draggableId={String(opportunity.id)}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={cn(snapshot.isDragging && "rotate-[1.5deg]")}
                    >
                      <OpportunityCard
                        opportunity={opportunity}
                        index={index}
                        compact
                        {...cardProps}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
            </KanbanDropZone>
          </KanbanColumn>
        );
      })}
    </KanbanBoard>
  );
};
